import React, { useState } from 'react';
import { Button, Form, Input, Modal, Space, notification } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { NumericFormat } from 'react-number-format';

import {
  useRegisterVehicleMutation,
  useSetPriceMutation,
  useUpdateVehicleStatusMutation,
} from '../../../app/api/vehicleApiSlice';
import VehicleStatus from '../../../core/enums/VehicleStatus';
import Vehicle from '../../../core/models/Vehicle';

interface IProps {
  vehicle: Vehicle;
}

const ChangeVehicleStatus: React.FC<IProps> = ({ vehicle }: IProps) => {
  const [registerVehicle] = useRegisterVehicleMutation();
  const [updateVehicleStatus, { isLoading: isStatusUpdateLoading }] =
    useUpdateVehicleStatusMutation();
  const [setPrice, { isLoading }] = useSetPriceMutation();
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);

  const handleRegisterVehicle = (): void => {
    Modal.confirm({
      title: 'Register Vehicle',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to register this vehicle with AutoStream auction?',
      okText: 'Yes',
      cancelText: 'Cancel',
      onOk: async () => {
        await registerVehicle(vehicle.id);
      },
    });
  };

  const handleSetPrice = (): void => {
    form
      .validateFields()
      .then((values) => {
        setPrice({
          id: vehicle.id,
          status: 6,
          startingPrice: parseFloat(values.startingPrice.replace(/,/g, '')),
          reservePrice: parseFloat(values.reservePrice.replace(/,/g, '')),
        })
          .unwrap()
          .then(() => {
            setVisible(false);
          })
          .catch((err) => {
            console.log(err);
          });
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  const handleWithdraw = (): void => {
    updateVehicleStatus({ id: vehicle.id, status: VehicleStatus.Withdrawn })
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Withdraw Vehicle',
          description: 'Vehicle withdrawn successfully',
        });
      })
      .catch((error) => {
        console.log(error);
      });
  };

  switch (vehicle.status) {
    case VehicleStatus.New:
      return (
        <Button type='primary' onClick={() => handleRegisterVehicle()}>
          Register Vehicle
        </Button>
      );
    case VehicleStatus.Rejected:
      return (
        <Button type='ghost' onClick={() => handleRegisterVehicle()}>
          Register Vehicle Again
        </Button>
      );
    case VehicleStatus.PriceListed:
    case VehicleStatus.GradingCompleted:
      return (
        <>
          <Button
            type='primary'
            danger
            onClick={() => {
              setVisible(true);
            }}
          >
            Update Price
          </Button>
          <Modal
            open={visible}
            title='Expected Price'
            okText='Submit'
            cancelText='Cancel'
            onCancel={() => {
              setVisible(false);
            }}
            footer={[
              <Button key='cancel' onClick={() => setVisible(false)}>
                Cancel
              </Button>,
              <Button
                key='submit'
                type='primary'
                onClick={() => handleSetPrice()}
                loading={isLoading}
              >
                Update
              </Button>,
            ]}
          >
            <Form
              form={form}
              layout='vertical'
              name='form_in_modal'
              initialValues={{
                startingPrice: vehicle.startingPrice.toString(),
                reservePrice: vehicle.reservePrice.toString(),
              }}
            >
              <Form.Item
                required={true}
                label='Starting Price'
                name='startingPrice'
                rules={[
                  {
                    required: true,
                    message: 'Required',
                  },
                ]}
              >
                <NumericFormat
                  customInput={Input}
                  thousandSeparator=','
                  decimalSeparator='.'
                  decimalScale={0}
                />
              </Form.Item>
              <Form.Item
                required={true}
                label='Reserve Price'
                name='reservePrice'
                rules={[
                  {
                    required: true,
                    message: 'Required',
                  },
                ]}
              >
                <NumericFormat
                  customInput={Input}
                  thousandSeparator=','
                  decimalSeparator='.'
                  decimalScale={0}
                />
              </Form.Item>
            </Form>
          </Modal>
        </>
      );
    // case VehicleStatus.Unsold:
    //   return (
    //     <Space>
    //       <Button type='primary' danger onClick={handleWithdraw} loading={isStatusUpdateLoading}>
    //         Withdraw
    //       </Button>
    //     </Space>
    //   );
    default:
      return <></>;
  }
};

export default ChangeVehicleStatus;
